import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Button } from 'antd';
import type { SubscriptionOverviewProps } from './types';
import { getCurrency, formatPrice } from './utils';
import './styles/index.less';

const SubscriptionOverview: React.FC<SubscriptionOverviewProps> = ({
  subscription: _subscription, // eslint-disable-line @typescript-eslint/no-unused-vars
  currentPlanData,
  subscriptionProducts = [],
  isLoading = false,
  onChangeTab,
  onChangePlanCategory,
  onShowCancelModal,
  onSetHideActionPanel,
  onRestoreSubscription,
  restoringSubscription = false,
  isEnterpriseUser = false,
}) => {
  const { t, i18n } = useTranslation();

  // 检查是否有计划数据
  // const hasPlanData = currentPlanData && currentPlanData.user_product_total && currentPlanData.user_product_total.length > 0;
  const hasPlanData = currentPlanData?.type === 'SUBSCRIPTION';
  const planType = currentPlanData?.type || 'NO_SUBSCRIPTION';

  // 获取订阅详情
  const subscriptionPlan = currentPlanData?.plan;
  const subscriptionStatus = subscriptionPlan?.status || 'INACTIVE';
  const productId = subscriptionPlan?.product_id || '';
  const subscriptionType = subscriptionPlan?.subscription_type || '';
  const nextChargeTime = subscriptionPlan?.charge_time || '';

  // 获取主要的产品总计数据
  const primaryProduct = currentPlanData?.user_product_total?.[0];
  const balance = primaryProduct?.balance || 0;
  const total = primaryProduct?.total || 0;
  // 计算已使用量的百分比
  const usedAmount = total - balance;
  const usagePercentage = total > 0 ? Math.round((usedAmount / total) * 100) : 0;

  // 检查是否有使用数据（即使没有订阅也可能有免费额度）
  const hasUsageData =
    currentPlanData?.user_product_total && currentPlanData.user_product_total.length > 0 && total > 0;

  // 获取计划名称
  const getPlanName = (id: string): string => {
    const currentProduct = subscriptionProducts.find((product) => product.product_id === id);
    return currentProduct?.name.split(' ').at(-1) || (id.split('_').at(-1) as string);
  };

  // 获取状态显示文本和样式
  const getStatusDisplay = (status: string) => {
    const statusMap: Record<string, { text: string; className: string }> = {
      ACTIVE: { text: t('account.active') || 'Active', className: 'active' },
      INACTIVE: { text: t('account.inactive') || 'Inactive', className: 'inactive' },
      CANCELED: { text: t('account.canceled') || 'Canceled', className: 'canceled' },
      CANCEL: { text: t('account.cancel') || 'Cancel', className: 'canceled' },
      PAST_DUE: { text: t('account.pastDue') || 'Past Due', className: 'past-due' },
    };
    return statusMap[status] || { text: status, className: 'inactive' };
  };

  // 获取当前订阅的价格信息
  const getCurrentSubscriptionPrice = () => {
    if (!productId || !subscriptionProducts.length) return 0;

    const currentProduct = subscriptionProducts.find((product) => product.product_id === productId);
    if (!currentProduct) return 0;

    const currency = getCurrency(i18n.language);
    const priceInfo = currentProduct.price_list.find((price) => price.currency === currency);

    return priceInfo?.price || 0;
  };

  const currentPrice = getCurrentSubscriptionPrice();

  // 获取状态样式类名
  const getStatusClassName = () => {
    if (subscriptionStatus === 'ACTIVE') return 'bg-purple-100 text-purple-700';
    if (subscriptionStatus === 'CANCEL' || subscriptionStatus === 'CANCELED') return 'bg-red-100 text-red-700';
    if (subscriptionStatus === 'PAST_DUE') return 'bg-yellow-100 text-yellow-700';
    return 'bg-gray-100 text-gray-700';
  };

  if (isLoading) {
    return (
      <div
        className="flex flex-col md:flex-row md:items-center md:justify-between bg-white rounded-2xl p-6
          w-full box-border border border-gray-100 gap-6 md:gap-8 animate-pulse"
        style={{ boxShadow: '0 0 4px rgba(0, 0, 0, 0.1)' }}
      >
        <div className="flex items-center gap-3 md:min-w-[200px]">
          <div className="bg-gray-200 rounded-full w-10 h-10"></div>
          <div className="flex-1">
            <div className="h-5 bg-gray-200 rounded w-24 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-32"></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-8 bg-gray-200 rounded w-16"></div>
          <div className="h-5 bg-gray-200 rounded w-20"></div>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="h-10 bg-gray-200 rounded w-24"></div>
          <div className="h-10 bg-gray-200 rounded w-28"></div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col bg-white rounded-2xl p-6 w-full box-border border border-gray-100 gap-6"
      style={{ boxShadow: '0 0 4px rgba(0, 0, 0, 0.1)' }}
    >
      {/* Main horizontal section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 md:gap-8">
        {/* Left section - icon and title */}
        <div className="flex items-center gap-3 md:min-w-[200px]">
          <div
            className="bg-purple-100 text-purple-700 rounded-full w-10 h-10 flex items-center justify-center
              font-semibold text-xl"
          >
            ⚡
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold my-0">
                {t('account.currentSubscription') || 'Current Subscription'}
              </h2>
              {hasPlanData && (
                <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusClassName()}`}>
                  {getStatusDisplay(subscriptionStatus).text}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 my-0">
              {t('account.manageYourSubscription') || 'Manage your subscription and usage'}
            </p>
          </div>
        </div>

        {/* Middle section - usage info or empty state */}
        <div className="flex items-center gap-2 text-lg font-semibold text-gray-800 md:flex-1 md:justify-center">
          {hasUsageData ? (
            <>
              <span className={`text-2xl font-bold ${usagePercentage > 80 ? 'text-red-600' : 'text-purple-600'}`}>
                {usagePercentage}%
              </span>
              <span className="text-gray-600">{t('account.used') || 'used'}</span>
              <span className="text-sm text-gray-500 ml-3">
                {Math.floor((total - balance) / 3600)} / {Math.floor(total / 3600)} {t('account.hours') || 'hours'}
              </span>
            </>
          ) : (
            <span className="text-sm text-gray-400">{t('account.noUsageData') || 'No usage data'}</span>
          )}
        </div>

        {/* Right section - buttons */}
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <Button
            onClick={() => {
              // 点击"更改计划"或"订阅计划"按钮，切换到定价页面
              onSetHideActionPanel?.(true);
              onChangeTab('change-plan');
            }}
            className="px-5 py-2 text-sm w-full sm:w-auto"
            style={{
              backgroundColor: 'white',
              borderColor: '#D1D5DB',
              color: '#374151',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F9FAFB';
              e.currentTarget.style.borderColor = '#D1D5DB';
              e.currentTarget.style.color = '#374151';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#D1D5DB';
              e.currentTarget.style.color = '#374151';
            }}
          >
            {hasPlanData
              ? t('account.changePlan') || 'Change Plan'
              : t('account.subscribeToPlan') || 'Subscribe to Plan'}
          </Button>
          {!isEnterpriseUser && (
            <Button
              onClick={() => {
                onSetHideActionPanel?.(true);
                onChangeTab('change-plan');
                onChangePlanCategory('addon');
              }}
              className="px-5 py-2 text-sm w-full sm:w-auto"
              style={{
                backgroundColor: 'white',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.color = '#374151';
              }}
            >
              {t('account.buyTopup') || 'Buy Top-up'}
            </Button>
          )}
          {planType === 'SUBSCRIPTION' && subscriptionStatus === 'ACTIVE' && (
            <Button onClick={onShowCancelModal} className="px-5 py-2 text-sm w-full sm:w-auto" type="primary" danger>
              {t('account.cancelSubscription') || 'Cancel Subscription'}
            </Button>
          )}
          {planType === 'SUBSCRIPTION' && subscriptionStatus === 'CANCEL' && onRestoreSubscription && (
            <Button
              onClick={onRestoreSubscription}
              loading={restoringSubscription}
              className="px-5 py-2 text-sm w-full sm:w-auto"
              style={{
                backgroundColor: 'white',
                borderColor: '#D1D5DB',
                color: '#374151',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F9FAFB';
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.color = '#374151';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.color = '#374151';
              }}
            >
              {t('account.restoreSubscription') || 'Restore Subscription'}
            </Button>
          )}
        </div>
      </div>

      {/* Subscription details section - only show when has plan data */}
      {hasPlanData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Plan card */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <svg
                className="w-5 h-5 text-purple-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
              <span className="font-medium text-gray-900">{t('account.plan') || 'Plan'}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{getPlanName(productId)}</p>
            <p className="text-sm text-gray-600">{subscriptionType}</p>
          </div>

          {/* Amount card */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <svg
                className="w-5 h-5 text-purple-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
              <span className="font-medium text-gray-900">{t('account.amount') || 'Amount'}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {formatPrice(currentPrice, getCurrency(i18n.language))}
              <span className="text-sm font-normal">
                /{subscriptionType === 'MONTHLY' ? t('account.month') || 'month' : t('account.year') || 'year'}
              </span>
            </p>
          </div>

          {/* Next billing card - only show for ACTIVE subscriptions */}
          {subscriptionStatus === 'ACTIVE' && nextChargeTime && (
            <div className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <svg
                  className="w-5 h-5 text-purple-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span className="font-medium text-gray-900">{t('account.nextBilling') || 'Next Billing'}</span>
              </div>
              <p className="text-sm font-semibold text-gray-900">{dayjs(nextChargeTime).format('MMMM DD, YYYY')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionOverview;
